/**
 * PDP gallery behaviours (client, 14 Aug 2026), paired with pdp-gallery.css.
 *
 * 1. DIRECTION for the slide animations: the theme's fader flips
 *    media--hidden / media--hiding classes but never says which way the user
 *    went. This observer compares the DOM index of the slide that started
 *    hiding with the newly active one and stamps data-slide-dir="next|prev"
 *    on the slide list BEFORE the browser paints, so the CSS animations run
 *    in the direction of travel. Thumbnail jumps get a direction too (by
 *    index), which reads naturally.
 *
 * 2. ADAPTIVE ARROW COLOUR: samples the active image in the zones under each
 *    chevron (canvas, CORS-safe re-fetch - the Shopify CDN sends
 *    Access-Control-Allow-Origin: *) and sets light grey over light imagery,
 *    faded white over dark, per side. Any failure (CORS, video slide, decode
 *    error) leaves the CSS fallback grey - a wrong-looking arrow is worse
 *    than a quietly default one.
 */
(function () {
  'use strict';

  var LIGHT = 'rgba(0, 0, 0, 0.28)'; /* light grey chevron for light imagery */
  var DARK = 'rgba(255, 255, 255, 0.75)'; /* faded white for dark imagery */

  function luminance(data, x0, x1, y0, y1, w) {
    var sum = 0;
    var n = 0;
    for (var y = y0; y < y1; y++) {
      for (var x = x0; x < x1; x++) {
        var i = (y * w + x) * 4;
        // alpha-weighted against white ground, as the CDN serves JPEGs on white
        var a = data[i + 3] / 255;
        var r = data[i] * a + 255 * (1 - a);
        var g = data[i + 1] * a + 255 * (1 - a);
        var b = data[i + 2] * a + 255 * (1 - a);
        sum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
        n++;
      }
    }
    return n ? sum / n / 255 : 1;
  }

  function sampleArrows(gallery) {
    var active = gallery.querySelector('.product__slide:not(.media--hidden):not(.media--hiding)');
    var img = active && active.querySelector('img');
    if (!img || !img.currentSrc) return;

    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var W = 48;
        var H = Math.max(1, Math.round((probe.naturalHeight / probe.naturalWidth) * W)) || 48;
        var canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0, W, H);
        var data = ctx.getImageData(0, 0, W, H).data;
        // the arrows sit mid-height at the left and right edges
        var y0 = Math.floor(H * 0.35);
        var y1 = Math.ceil(H * 0.65);
        var lumPrev = luminance(data, 0, Math.ceil(W * 0.18), y0, y1, W);
        var lumNext = luminance(data, Math.floor(W * 0.82), W, y0, y1, W);
        gallery.style.setProperty('--pdp-arrow-color-prev', lumPrev > 0.55 ? LIGHT : DARK);
        gallery.style.setProperty('--pdp-arrow-color-next', lumNext > 0.55 ? LIGHT : DARK);
      } catch (e) {
        /* tainted canvas or decode failure: keep the CSS fallback */
      }
    };
    probe.src = img.currentSrc;
  }

  function init(gallery) {
    if (gallery.hasAttribute('data-pdp-gallery-init')) return;
    gallery.setAttribute('data-pdp-gallery-init', '');

    var list = gallery.querySelector('[data-product-media-list]');
    if (!list) return;

    var slides = function () {
      return Array.prototype.slice.call(list.querySelectorAll('.product__slide'));
    };

    var pending = null;
    var observer = new MutationObserver(function (mutations) {
      var incoming = null;
      var outgoing = null;
      mutations.forEach(function (m) {
        if (m.type !== 'attributes' || m.attributeName !== 'class') return;
        var el = m.target;
        var was = m.oldValue || '';
        var is = el.className;
        if (was.indexOf('media--hiding') === -1 && is.indexOf('media--hiding') !== -1) outgoing = el;
        if (
          (was.indexOf('media--hidden') !== -1 || was.indexOf('media--hiding') !== -1) &&
          is.indexOf('media--hidden') === -1 &&
          is.indexOf('media--hiding') === -1
        ) incoming = el;
      });
      if (incoming && outgoing && incoming !== outgoing) {
        var all = slides();
        list.setAttribute('data-slide-dir', all.indexOf(incoming) > all.indexOf(outgoing) ? 'next' : 'prev');
      }
      if (incoming) {
        clearTimeout(pending);
        pending = setTimeout(function () { sampleArrows(gallery); }, 120);
      }
    });
    observer.observe(list, { attributes: true, attributeOldValue: true, attributeFilter: ['class'], subtree: true });

    sampleArrows(gallery);
  }

  function start(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('product-images'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { start(); });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (e) { start(e.target || document); });
})();
